import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Zap, ArrowRight } from "lucide-react";

/**
 * SignUpPrompt
 * A dismissible sign-up banner/modal for unauthenticated users
 * Props:
 *   trigger: "quiz" | "factcheck" | "general"
 *   onDismiss: fn
 */
export function SignUpPrompt({ trigger = "general", onDismiss }) {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const dismiss = () => { setDismissed(true); onDismiss?.(); };

  const CONTENT = {
    quiz: {
      heading: "Save your quiz result",
      body: "Sign up to save your party match history and see how your political views shift over time.",
      cta: "Save my result",
    },
    factcheck: {
      heading: "Unlimited fact-checks",
      body: "You've been busy! Sign up to get unlimited AI fact-checks and save your verdicts.",
      cta: "Get unlimited access",
    },
    follow: {
      heading: "Follow this politician",
      body: "Sign up to follow politicians, track their promises, and get updates when they vote.",
      cta: "Sign up to follow",
    },
    general: {
      heading: "Personalise AusVote",
      body: "Sign up to unlock your electorate profile, follow politicians, set your issues, and check your election readiness.",
      cta: "Create free account",
    },
  };

  const c = CONTENT[trigger] || CONTENT.general;

  return (
    <div
      className="border-2 border-black p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 relative"
      style={{ backgroundColor: "#0F172A" }}
      data-testid="signup-prompt"
    >
      <div className="flex-shrink-0">
        <Zap className="w-6 h-6" style={{ color: "#FFC82E" }} />
      </div>
      <div className="flex-1">
        <p className="font-black text-sm text-white mb-0.5">{c.heading}</p>
        <p className="text-xs" style={{ color: "#94A3B8" }}>{c.body}</p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={() => navigate("/auth")}
          className="flex items-center gap-1.5 text-xs font-black px-3 py-2 border-2 border-black hover:-translate-y-0.5 transition-transform"
          style={{ backgroundColor: "#FFC82E", color: "#000", boxShadow: "2px 2px 0 #000" }}
          data-testid="signup-prompt-cta"
        >
          {c.cta} <ArrowRight className="w-3 h-3" />
        </button>
        <button
          onClick={dismiss}
          className="text-xs font-bold px-2 py-2 border-2 border-white text-white hover:opacity-75"
          data-testid="signup-prompt-dismiss"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}
